import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import './empty-state.scss'

// An action on an empty state. Pass the page's EXISTING create/add action --
// the same route or handler its header menu uses -- and gate it with `show`
// on the same permission, so the zero state never offers something the
// viewer cannot do. `show: false` (or no label) renders nothing.
//   { label, to }            -> a router link
//   { label, onClick }       -> a button
const isShown = action => !!action && !!action.label && action.show !== false && (!!action.to || !!action.onClick)

const EmptyStateAction = ({ action, primary }) => {
  const className = primary ? 'btn btn-sm es-btn-primary' : 'btn btn-sm es-btn-secondary'
  const Icon = action.Icon

  const content = (
    <React.Fragment>
      {Icon && <Icon className="me-1" />}
      {action.label}
    </React.Fragment>
  )

  if (action.to) {
    return (
      <Link className={className} to={action.to}>
        {content}
      </Link>
    )
  }

  return (
    <button type="button" className={className} onClick={action.onClick}>
      {content}
    </button>
  )
}

// The one zero state used by list, card and grid pages: an icon, a sentence-case
// title, a one-sentence explanation, an optional primary action and an optional
// secondary action (an outlined button, never a bare text link). `compact` is for small containers such as dashboard tiles.
//
// Only the text is a live region (role=status); the actions sit outside it,
// so assistive tech announces the explanation without reading controls.
// `animate={false}` skips the entrance animation (it is also skipped under
// prefers-reduced-motion).
export const EmptyState = ({ Icon, title, message, action, secondary, compact, className, animate = true, testID = 'empty-state' }) => {
  const classes = ['es-root', animate ? 'es-animate' : '', compact ? 'es-compact' : '', className || ''].filter(Boolean).join(' ')
  const showPrimary = isShown(action)
  const showSecondary = isShown(secondary)

  return (
    <div className={classes} data-testid={testID}>
      {Icon &&
        <span className="es-icon" aria-hidden="true">
          <Icon />
        </span>}
      <div className="es-text" role="status">
        {title && <h2 className="es-title">{title}</h2>}
        {message && <p className="es-message">{message}</p>}
      </div>
      {(showPrimary || showSecondary) &&
        <div className="es-actions">
          {showPrimary && <EmptyStateAction action={action} primary />}
          {showSecondary && <EmptyStateAction action={secondary} />}
        </div>}
    </div>
  )
}

// A loading placeholder (skeleton rows) so a list never shows as a blank area
// while its first fetch is in flight.
export const LoadingState = ({ rows = 6, label = 'Loading' }) =>
  <div className="es-loading" role="status" aria-live="polite" data-testid="loading-state">
    <span className="visually-hidden">{label}</span>
    {Array.from({ length: rows }).map((_, i) => <div key={i} className="es-skeleton-row" aria-hidden="true" data-testid="skeleton-row" />)}
  </div>

// True once an api-new list (useApiList / useApiListChunk) has finished its
// first fetch, successfully or not. It stays true through later reloads and
// polls, so a refresh never swaps the page back to a loading placeholder, and
// an empty state never flashes before the first answer arrives.
export const useFirstLoadSettled = apiList => {
  const done = !!apiList && (!!apiList.loaded || apiList.error !== undefined)
  const [settled, updateSettled] = useState(done)

  useEffect(
    () => {
      if (done && !settled) updateSettled(true)
    },
    [done, settled]
  )

  return settled || done
}
